/* ---- Spoofing alert banner ----
 *
 * Phase 4 P3-iOS: `availability` メタ情報対応。
 *
 *  - iOS は 3 検知器のみ実装 (PressureAltitude / IMU / MockDetection)。
 *    SDK は wire format に `availability: { ... false }` を付与する。
 *  - Android は 7 検知器 + mock_location すべて実測 (`availability` 省略 = 全 true 扱い)。
 *
 *  WebView 側はバナーで「警告 (warning/critical)」のみ表示する責務のため、
 *  unavailable 検知器は警告候補から除外する (false positive 防止)。
 *
 * 参照:
 *   - src/ios/sdk/Sources/GeoAttestSDK/Antispoofing/SpoofingStatusMapper.swift
 *   - 設計/ADR/ADR-0007-ios-detector-reduction.md
 */

import { ICONS } from '../icons';
import type { DetectorStatus, SpoofingStatus } from '../types';

/** SpoofingStatus 上の検知器キー (availability map と共通の 9 種) */
type DetectorKey =
  | 'doppler'
  | 'imu'
  | 'signal_quality'
  | 'time'
  | 'signal_structure'
  | 'pressure_altitude'
  | 'doppler_raim'
  | 'qzss_authentication'
  | 'mock_location';

const ALL_DETECTOR_KEYS: readonly DetectorKey[] = [
  'doppler',
  'imu',
  'signal_quality',
  'time',
  'signal_structure',
  'pressure_altitude',
  'doppler_raim',
  'qzss_authentication',
  'mock_location',
] as const;

const ALGO_NAMES: Record<DetectorKey, string> = {
  doppler: 'Doppler Consistency',
  imu: 'IMU Consistency',
  signal_quality: 'Signal Quality',
  time: 'Time Consistency',
  signal_structure: 'Signal Structure',
  pressure_altitude: 'Pressure Altitude',
  doppler_raim: 'Doppler RAIM',
  qzss_authentication: 'QZSS Authentication',
  mock_location: 'Mock Location',
};

const ALGO_DESCRIPTIONS: Record<DetectorKey, string> = {
  doppler: 'ドップラー効果の整合性に異常を検出。位置情報の信頼性が低下しています。',
  imu: '慣性計測装置の整合性に異常を検出。位置情報の信頼性が低下しています。',
  signal_quality: '信号品質に異常を検出。位置情報の信頼性が低下しています。',
  time: '時刻整合性に異常を検出。位置情報の信頼性が低下しています。',
  signal_structure: '信号構造の整合性に異常を検出。位置情報の信頼性が低下しています。',
  pressure_altitude: '気圧高度に異常を検出。位置情報の信頼性が低下しています。',
  doppler_raim: 'Doppler RAIM に異常を検出。位置情報の信頼性が低下しています。',
  qzss_authentication: 'QZSS 認証信号に異常を検出。位置情報の信頼性が低下しています。',
  mock_location: 'モック位置情報（位置偽装アプリ）を検出。位置情報は信頼できません。',
};

/**
 * 検知器の表示状態を返す。
 *
 * - `availability[key] === false` → `'unsupported'` (iOS 由来の未対応)
 * - 値が `'warning'` / `'critical'` → そのまま (異常)
 * - 値が `'ok'` または `undefined` → `'ok'` (正常 / 未受信)
 *
 * @param status   SpoofingStatus 全体
 * @param key      対象検知器キー
 */
export function getDetectorDisplayState(
  status: SpoofingStatus,
  key: DetectorKey,
): 'unsupported' | DetectorStatus {
  if (status.availability && status.availability[key] === false) {
    return 'unsupported';
  }
  return (status[key] as DetectorStatus | undefined) ?? 'ok';
}

/**
 * `availability` を考慮した警告判定。`unsupported` は警告対象から除外する。
 */
export function isDetectorWarningLike(status: SpoofingStatus, key: DetectorKey): boolean {
  const s = getDetectorDisplayState(status, key);
  return s === 'warning' || s === 'critical';
}

export function renderSpoofingAlert(
  status: SpoofingStatus,
  onClose: () => void,
): HTMLElement | null {
  // availability=false の検知器は警告候補から除外 (unsupported を warning と誤判定しない)
  const warnings = ALL_DETECTOR_KEYS.filter((k) => isDetectorWarningLike(status, k));

  if (warnings.length === 0) return null;

  const firstWarning = warnings[0];
  const el = document.createElement('div');
  el.className = 'ab';
  el.innerHTML = `
    <div style="flex-shrink:0;margin-top:1px">${ICONS.alert}</div>
    <div class="bd">
      <div class="at">位置欺瞞を検知しました</div>
      <div class="as">検知項目: <b>${ALGO_NAMES[firstWarning]}</b></div>
      <div class="ad">${ALGO_DESCRIPTIONS[firstWarning]}</div>
    </div>
    <button class="cl">${ICONS.close}</button>
  `;

  el.querySelector('.cl')!.addEventListener('click', onClose);
  return el;
}
